import * as Slider from '@radix-ui/react-slider';
import cn from 'classnames';
import { find } from 'ramda';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { generatePath, useLocation, useNavigate } from 'react-router';

import { ConditionCode } from '@/shared/api/protocol-ts/model/dto_report_condition_codes_pb';
import { Condition } from '@/shared/api/protocol-ts/model/dto_report_condition_pb';
import {
  Report,
  ReportType,
} from '@/shared/api/protocol-ts/model/dto_report_pb';
import { Tooth } from '@/shared/api/protocol-ts/model/dto_report_tooth_pb';
import {
  DEFAULT_MEDICAL_IMAGE_HEIGHT,
  LocationStateType,
  PATHS,
} from '@/shared/config';
import {
  SelectedSlicesMIR,
  SlicesMIR,
} from '@/shared/graphics/medicalImageRender/MedicalImageRender';
import { useAppDispatch, useAppSelector, useMedia } from '@/shared/hooks';
import { scrollToTop, trackEvent } from '@/shared/lib';
import { Button, toastCaller, WidgetCard } from '@/shared/ui';

import {
  CompactCondition,
  ConditionButton,
  ConditionGroups,
  conditionText,
  conditionModel,
  ConditionUserScoreInput,
  FDA_VERSION2_AUTODETECTED_ENDO_CHILD_CODES,
  getFDAVersion2ConditionCodes,
  isUserPositiveCertainty,
  shouldConditionRequireUserScore,
  useToothConditions,
} from '@/entities/condition';
import { ModalID, modalModel } from '@/entities/modal';
import { organizationModel } from '@/entities/organization';
import {
  FMXNavigationMode,
  isReport2D,
  reportsModel,
} from '@/entities/reports';
import {
  getDisplayToothNumber,
  getToothTitleNumber,
  toothModel,
} from '@/entities/tooth';

import { useUpdateConditionDecision } from '@/features/updateConditionDecision';

import { ApproveToothButton } from '../ApproveToothButton/ApproveToothButton';

import styles from './ToothCardFDA.module.scss';

type ToothCardProps = {
  className?: string;
  id: string;
  patientID: string;
  reportID: string;
  showViewerButton?: boolean;
  medicalImages?: React.ReactNode;
  dndComponent?: React.ReactNode;
  showSlices?: boolean;
  handleCardClick?: (ID: string) => void;
  isToothChartMode?: boolean;
  onSlicesClick?: (toothID: string) => void;
  imageContainerComponent?: React.ReactNode;
};

export const ToothCardFDA: FC<ToothCardProps> = (props) => {
  const {
    className,
    id,
    reportID,
    patientID,
    dndComponent,
    showViewerButton = true,
    showSlices = true,
    onSlicesClick,
    imageContainerComponent,
  } = props;

  const [sliderValue, setSliderValue] = useState<number | undefined>(undefined);
  const [error, setError] = useState<boolean>(false);
  // FDA 2.0 manual confidence input: local per-condition values keyed by condition ID
  const [userScores, setUserScores] = useState<
    Record<string, number | undefined>
  >({});
  const [errorConditionIDs, setErrorConditionIDs] = useState<string[]>([]);

  const handleSliderChange = (value: number[]) => {
    setSliderValue(value[0]);
    setError(false);
  };

  const {
    isUnaided: isFDANonAided,
    manualPercentInput,
    periapicalConditionVisible,
    confidenceSliderVisible,
    masksVisible,
  } = useAppSelector(organizationModel.selectors.selectFDAEnvironment);

  const { formatMessage } = useIntl();
  const { isMobile } = useMedia();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();

  const report = useAppSelector(reportsModel.selectors.selectByID(reportID));
  const tooth = useAppSelector((state) =>
    toothModel.selectors.selectById(state, id),
  );

  const { updateConditionDecision } = useUpdateConditionDecision(id);
  const navigationMode = useAppSelector(
    reportsModel.selectors.selectNavigationMode,
  );
  const activeToothID = useAppSelector(
    reportsModel.selectors.selectActiveToothID,
  );

  // Memoize selector instances: these are createSelector factories, so calling
  // them inline rebuilds the selector each render and returns fresh references,
  // which would retrigger the score-seeding effect into an infinite loop.
  const periapicalConditionSelector = useMemo(
    () =>
      conditionModel.selectors.selectPeriapicalConditionByToothID(
        tooth?.ID ?? '',
      ),
    [tooth?.ID],
  );
  const periapicalCondition = useAppSelector(periapicalConditionSelector);

  const dentalNotationFormat = useAppSelector(
    organizationModel.selectors.selectDentalNotationFormat,
  );

  const displayToothNumber = getDisplayToothNumber(
    tooth?.Numeration?.ISO ?? 0,
    dentalNotationFormat,
  ).toString();
  const ISONumber = tooth?.Numeration?.ISO;
  const titleToothNumber = getToothTitleNumber({
    numeration: tooth?.Numeration,
    dentalNotationFormat,
  });

  // type casting is necessary for typification location.state
  const locationState = location?.state as LocationStateType;

  const reportType = report?.Type;

  const isReportWithViewModes =
    reportType === ReportType.ReportType_IOXRay_GP ||
    reportType === ReportType.ReportType_Pano_Bitewings;

  const isToothFocused = tooth?.ID === activeToothID;
  const isFocusedView = isReportWithViewModes
    ? isToothFocused && navigationMode === FMXNavigationMode.ToothChart
    : isToothFocused;

  const { anatomyConditionItems, generalConditionItems } = useToothConditions({
    toothID: tooth?.ID ?? '',
  });

  const toothConditionsSelector = useMemo(
    () => conditionModel.selectors.selectByToothID(tooth?.ID ?? ''),
    [tooth?.ID],
  );
  const toothConditions = useAppSelector(toothConditionsSelector);

  const is2DReport = isReport2D(reportType);

  const isUserPositiveCondition = useCallback(
    (item: CompactCondition) => {
      const itemIDs = new Set([item.id, ...item.ids].filter(Boolean));

      return toothConditions.some((condition) => {
        const isSameCondition =
          itemIDs.size > 0
            ? itemIDs.has(condition.ID)
            : condition.Code === item.code;

        if (!isSameCondition) {
          return false;
        }

        return isUserPositiveCertainty(condition.Certainty);
      });
    },
    [toothConditions],
  );

  // FDA 2.0 shows only the dedicated per-modality conditions list (spec section 3).
  // Allowed child conditions (Short filling, Overfilling, ...) are independent
  // diagnoses there, so they are flattened into their own rows.
  const cardConditionItems = useMemo(() => {
    const allItems = [...anatomyConditionItems, ...generalConditionItems];

    if (!manualPercentInput) {
      return allItems;
    }

    const fdaAllowedCodes = getFDAVersion2ConditionCodes(is2DReport);

    const fdaItems = allItems
      .filter((item) => fdaAllowedCodes.includes(item.code))
      .flatMap((item) => {
        const isParentEnabled = isUserPositiveCondition(item);
        const children = item.childConditionInterfaces.filter((childItem) => {
          if (!fdaAllowedCodes.includes(childItem.code)) {
            return false;
          }

          if (
            !isFDANonAided &&
            FDA_VERSION2_AUTODETECTED_ENDO_CHILD_CODES.includes(childItem.code)
          ) {
            return isParentEnabled || childItem.isChecked;
          }

          return isParentEnabled;
        });

        return [item, ...children];
      });

    if (isFDANonAided) {
      return fdaItems.filter(isUserPositiveCondition);
    }

    return fdaItems;
  }, [
    anatomyConditionItems,
    generalConditionItems,
    isFDANonAided,
    isUserPositiveCondition,
    manualPercentInput,
    is2DReport,
  ]);

  const scoreableConditionItems = useMemo(
    () =>
      manualPercentInput
        ? cardConditionItems.filter((item) =>
            shouldConditionRequireUserScore({ code: item.code, is2DReport }),
          )
        : [],
    [cardConditionItems, manualPercentInput, is2DReport],
  );

  // Memoized so it can be a stable dependency of the seed effect below.
  const getSavedUserScore = useCallback(
    (conditionID: string) =>
      toothConditions.find((condition) => condition.ID === conditionID)
        ?.Certainty?.UserScore,
    [toothConditions],
  );

  // Seed local inputs from already saved Certainty.UserScore values
  useEffect(() => {
    if (!manualPercentInput) {
      return;
    }

    // Seed from currentScores itself (not a copy): when nothing new is seeded the
    // reduce returns the same reference, so setState bails out of a re-render.
    // The tooth-condition selectors return fresh arrays each render, so a new
    // object here would retrigger this effect and loop indefinitely.
    setUserScores((currentScores) =>
      scoreableConditionItems.reduce((seededScores, item) => {
        const savedUserScore = getSavedUserScore(item.id);

        if (
          seededScores[item.id] === undefined &&
          savedUserScore !== undefined
        ) {
          return { ...seededScores, [item.id]: savedUserScore };
        }

        return seededScores;
      }, currentScores),
    );
  }, [manualPercentInput, scoreableConditionItems, getSavedUserScore]);

  const handleUserScoreChange = (
    conditionID: string,
    value: number | undefined,
  ) => {
    setUserScores((currentScores) => ({
      ...currentScores,
      [conditionID]: value,
    }));
    setErrorConditionIDs((currentIDs) =>
      currentIDs.filter((id) => id !== conditionID),
    );
  };

  const onConditionHoverHandler = useCallback(
    (conditionItem?: CompactCondition) => {
      // FDA unaided: no masks and no bounding boxes anywhere in the report
      if (!masksVisible) {
        return;
      }

      if (!conditionItem) {
        dispatch(conditionModel.actions.setHoveredConditionIDs([]));
        dispatch(toothModel.actions.setHoveredToothID(''));
        return;
      }

      // Tooth-like rows have no own localizations: highlight the tooth bbox
      // through the hovered tooth pipeline instead
      if (conditionItem.group === ConditionGroups.toothType) {
        dispatch(toothModel.actions.setHoveredToothID(id));
        return;
      }

      if (
        conditionItem.hasLocalizations ||
        conditionItem.code === ConditionCode.EndoTreated ||
        conditionItem.code === ConditionCode.CrownDefectOver50pct
      ) {
        const conditionIDs = [conditionItem.id, ...conditionItem.ids].filter(
          Boolean,
        );

        dispatch(conditionModel.actions.setHoveredConditionIDs(conditionIDs));
        return;
      }

      dispatch(conditionModel.actions.setHoveredConditionIDs([]));
    },
    [dispatch, id, masksVisible],
  );

  useEffect(
    () => () => {
      dispatch(conditionModel.actions.setHoveredConditionIDs([]));
      dispatch(toothModel.actions.setHoveredToothID(''));
    },
    [dispatch],
  );

  const hasUnsavedUserScores = scoreableConditionItems.some(
    (item) => getSavedUserScore(item.id) !== userScores[item.id],
  );

  const showMultiConditionApproved =
    !!tooth?.IsApproved && !hasUnsavedUserScores;

  const periapicalRadiolucencyCondition = generalConditionItems.find(
    (item) => item.code === ConditionCode.PeriapicalRadiolucency,
  );

  const isUserScoreUpdated = useMemo(() => {
    return (
      periapicalCondition &&
      periapicalCondition.Certainty?.UserScore !== sliderValue
    );
  }, [periapicalCondition, sliderValue]);

  useEffect(() => {
    if (
      periapicalCondition &&
      periapicalCondition.Certainty?.UserScore !== undefined
    ) {
      setSliderValue(periapicalCondition.Certainty?.UserScore);
    } else {
      setSliderValue(undefined);
    }
  }, [periapicalCondition]);

  const toothTitleConditionItems = isFDANonAided
    ? cardConditionItems
    : anatomyConditionItems;
  const toothTitleCondition = find(
    (item) => item.group === ConditionGroups.toothType,
    toothTitleConditionItems,
  );
  const toothTitleConditionText =
    toothTitleCondition?.code !== undefined &&
    toothTitleCondition.code in conditionText
      ? formatMessage(conditionText[toothTitleCondition.code])
      : 'Tooth';
  const toothTitle = `${toothTitleConditionText} ${titleToothNumber}`;
  const shouldRenderFDA2DefaultToothCard = manualPercentInput && Boolean(tooth);
  const shouldRenderToothTitle =
    toothTitleCondition || isFDANonAided || shouldRenderFDA2DefaultToothCard;

  const isMissing = toothTitleCondition?.code === ConditionCode.Missing;

  const isMissingApproved = isMissing && tooth?.IsApproved;

  const isPeriapicalApproved =
    !isUserScoreUpdated &&
    periapicalCondition?.Certainty?.UserScore !== undefined &&
    tooth?.IsApproved;

  const showApproved = isMissingApproved || isPeriapicalApproved;

  useEffect(() => {
    if (isMissing) {
      setSliderValue(100);
    }
  }, [isMissing]);

  const isToothPage = location.pathname.includes('/tooth/');

  const handleAddCondition = useCallback(
    (conditionID?: string) => {
      trackEvent({
        eventName: isToothPage
          ? 'ToothPageAddConditionsButtonClicked'
          : 'ReportPageAddConditionsButtonClicked',
        properties: {
          tooth_number: ISONumber,
        },
      });

      if (report && tooth) {
        dispatch(
          modalModel.actions.openModal({
            modalID: ModalID.AddCondition,
            data: {
              toothID: tooth.ID,
              displayToothNumber,
              reportID,
              ISONumber,
              conditionID,
            },
          }),
        );
      }
    },
    [
      ISONumber,
      dispatch,
      displayToothNumber,
      isToothPage,
      report,
      reportID,
      tooth,
    ],
  );

  const approveUserScoreHandle = async () => {
    if (sliderValue === undefined) {
      setError(true);

      toastCaller({
        type: 'error',
        heading: <FormattedMessage id="error.heading" defaultMessage="Error" />,
        message:
          'Required evaluation of periapical radiolucency presence probability',
        autoClose: 10000,
      });

      return;
    }

    const conditionID = isMissing
      ? toothTitleCondition?.id
      : periapicalCondition?.ID;

    const { Condition, Tooth, Report } = await dispatch(
      reportsModel.thunks.setReportToothConditionUserScoreAndApproveTooth({
        ConditionID: conditionID as string,
        UserScore: sliderValue as number,
      }),
    ).unwrap();

    dispatch(reportsModel.actions.setNewestOne(Report as Report));
    dispatch(toothModel.actions.setOne(Tooth as Tooth));
    dispatch(conditionModel.actions.setNewestOne(Condition as Condition));
  };

  // FDA 2.0: approve is blocked until every diagnosis has a confidence percent
  const approveMultiConditionUserScoresHandle = async () => {
    const missingScoreConditionIDs = scoreableConditionItems
      .filter((item) => userScores[item.id] === undefined)
      .map((item) => item.id);

    if (missingScoreConditionIDs.length > 0) {
      setErrorConditionIDs(missingScoreConditionIDs);

      toastCaller({
        type: 'error',
        heading: <FormattedMessage id="error.heading" defaultMessage="Error" />,
        message: (
          <FormattedMessage
            id="toothCardFDA.userScoreRequired"
            defaultMessage="Please enter the confidence percent for every diagnosis before approving"
          />
        ),
        autoClose: 10000,
      });

      return;
    }

    setErrorConditionIDs([]);

    // Tooth-like objects (Tooth, Implant, Missing, ...) need no percent — plain approve
    if (scoreableConditionItems.length === 0) {
      await dispatch(
        reportsModel.thunks.setReportToothApproved({
          ToothID: tooth?.ID as string,
          ToothConditionIDs: [],
        }),
      );

      return;
    }

    // Sequential chain: each call also approves the tooth, parallel calls would
    // race on entity revisions. Store updates happen in thunkResponseMiddleware.
    try {
      await scoreableConditionItems.reduce(
        (chain, item) =>
          chain.then(() =>
            dispatch(
              reportsModel.thunks.setReportToothConditionUserScoreAndApproveTooth(
                {
                  ConditionID: item.id,
                  UserScore: userScores[item.id] as number,
                },
              ),
            )
              .unwrap()
              .then(() => undefined),
          ),
        Promise.resolve<void | undefined>(undefined),
      );
    } catch {
      toastCaller({
        type: 'error',
        heading: <FormattedMessage id="error.heading" defaultMessage="Error" />,
        message: (
          <FormattedMessage
            id="toothCardFDA.approveFailed"
            defaultMessage="Could not save all confidence percents. Please try again."
          />
        ),
        autoClose: 10000,
      });
    }
  };

  const handle3DSlicesClick = () => {
    scrollToTop();
    navigate(
      generatePath(PATHS.gpReportAllSlices, {
        patientID,
        reportID,
        toothID: tooth?.ID,
      }),
      {
        state: {
          ...locationState,
          toothID: tooth?.ID,
        },
        relative: 'path',
      },
    );
  };

  if (!shouldRenderToothTitle) {
    return null;
  }

  const useHandleOpenModal = (assetID: string) => {
    if (onSlicesClick) {
      onSlicesClick(tooth?.ID as string);
    }

    if (tooth) {
      dispatch(
        modalModel.actions.openModal({
          modalID: ModalID.MIRenderModal,
          data: { assetID, toothID: tooth.ID },
        }),
      );
    }
  };

  return (
    <WidgetCard
      id={tooth?.ID}
      data-component="tooth-card-fda"
      data-id={tooth?.ID}
      data-tooth-number={ISONumber ? String(ISONumber) : undefined}
      data-testid={ISONumber ? `tooth-card-${ISONumber}` : undefined}
      className={cn(
        styles.container,
        isFocusedView && styles.focused,
        className,
      )}
    >
      <header className={styles.header}>
        <h4
          className={cn('h4')}
          onMouseEnter={() =>
            dispatch(toothModel.actions.setHoveredToothID(id))
          }
          onMouseLeave={() =>
            dispatch(toothModel.actions.setHoveredToothID(''))
          }
        >
          {toothTitle}
        </h4>
      </header>
      {manualPercentInput && (
        <div className={cn(styles.conditions)}>
          {cardConditionItems
            .filter(
              (item) =>
                item.group !== ConditionGroups.toothType ||
                scoreableConditionItems.includes(item),
            )
            .map((item) => (
              <div key={item.id} className={styles.conditionRow}>
                <ConditionButton
                  conditionInterface={item}
                  onClick={handleAddCondition}
                  onHoverHandle={onConditionHoverHandler}
                  updateConditionDecision={updateConditionDecision}
                />
                {scoreableConditionItems.includes(item) && (
                  <ConditionUserScoreInput
                    value={userScores[item.id]}
                    error={errorConditionIDs.includes(item.id)}
                    onChange={(value) => handleUserScoreChange(item.id, value)}
                  />
                )}
              </div>
            ))}
        </div>
      )}
      {periapicalConditionVisible &&
        !isFDANonAided &&
        periapicalRadiolucencyCondition && (
          <div className={cn(styles.conditions)}>
            <ConditionButton
              conditionInterface={periapicalRadiolucencyCondition}
              onClick={handleAddCondition}
              onHoverHandle={onConditionHoverHandler}
              updateConditionDecision={updateConditionDecision}
            />
          </div>
        )}
      {showSlices &&
        tooth &&
        (imageContainerComponent ? (
          <SlicesMIR
            className={styles.imageList}
            toothID={tooth.ID}
            onClick={useHandleOpenModal}
          >
            {imageContainerComponent}
          </SlicesMIR>
        ) : (
          <SelectedSlicesMIR
            toothID={tooth.ID}
            className={styles.imageList}
            onClick={useHandleOpenModal}
            imageAttributes={{
              className: styles.slice,
              draggable: false,
              style: { height: DEFAULT_MEDICAL_IMAGE_HEIGHT + 'px' },
            }}
          />
        ))}

      {confidenceSliderVisible && !isMissing && (
        <div className={styles.sliderContainer}>
          <div className={styles.sliderHeader}>
            <div className={styles.label}>
              Your confidence in presence of Periapical radiolucency
            </div>
            <input
              type="number"
              min="0"
              max="100"
              value={sliderValue !== undefined ? sliderValue : ''}
              onChange={(e) => {
                const value = Math.min(
                  100,
                  Math.max(0, Number(e.target.value)),
                );
                setSliderValue(isNaN(value) ? undefined : value);
              }}
              placeholder="___"
              className={cn(
                styles.sliderInput,
                'h7',
                error && styles.inputError,
              )}
            />
            <div className={styles.sliderValue}>&nbsp;%</div>
          </div>
          <Slider.Root
            value={sliderValue !== undefined ? [sliderValue] : [50]}
            onValueChange={handleSliderChange}
            max={100}
            step={1}
            className={styles.sliderRoot}
          >
            <Slider.Track
              className={cn(
                styles.sliderTrack,
                sliderValue === undefined && styles.defaultTrack,
                error && styles.trackError,
              )}
            >
              <Slider.Range
                className={cn(
                  styles.sliderRange,
                  sliderValue === undefined && styles.defaultRange,
                  error && styles.rangeError,
                )}
              />
            </Slider.Track>
            <Slider.Thumb
              className={cn(
                styles.sliderThumb,
                sliderValue === undefined && styles.defaultThumb,
                error && styles.thumbError,
              )}
              aria-label="Probability"
            />
          </Slider.Root>
          {sliderValue !== undefined &&
            (sliderValue >= 50 ? (
              <div className={styles.redText}>
                You estimate that Periapical radiolucency is likely present
              </div>
            ) : (
              <div className={styles.greenText}>
                You estimate that Periapical radiolucency is likely not present
              </div>
            ))}
        </div>
      )}
      <footer className={styles.footer}>
        <div className={styles.leftFooterWrapper}>
          <Button
            size="medium"
            variant="gray"
            icon="plus"
            onClick={() => handleAddCondition()}
          >
            {!isMobile && (
              <FormattedMessage
                id="toothCard.openAddConditionModalButton"
                defaultMessage="Condition"
              />
            )}
          </Button>
        </div>

        <div className={styles.rightFooterWrapper}>
          {reportType === ReportType.ReportType_CBCT_GP && showViewerButton && (
            <Button
              variant="tertiary"
              size="medium"
              onClick={handle3DSlicesClick}
            >
              <FormattedMessage id="toothCard.slices" defaultMessage="Slices" />
            </Button>
          )}
          {manualPercentInput || confidenceSliderVisible ? (
            <Button
              variant="secondary"
              size="medium"
              onClick={
                manualPercentInput
                  ? approveMultiConditionUserScoresHandle
                  : approveUserScoreHandle
              }
              disabled={!report?.YourPermissions?.CanChangeToothApproved}
              success={
                manualPercentInput ? showMultiConditionApproved : showApproved
              }
            >
              {(
                manualPercentInput ? showMultiConditionApproved : showApproved
              ) ? (
                <FormattedMessage
                  id="toothCard.approved"
                  defaultMessage="Approved"
                />
              ) : (
                <FormattedMessage
                  id="toothCard.approve"
                  defaultMessage="Approve"
                />
              )}
            </Button>
          ) : (
            <ApproveToothButton
              toothID={tooth?.ID ?? ''}
              isApproved={Boolean(tooth?.IsApproved)}
              disabled={!report?.YourPermissions?.CanChangeToothApproved}
              isToothCard
            />
          )}
        </div>
      </footer>

      {dndComponent}
    </WidgetCard>
  );
};
